import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, User, ArrowRight, Phone, MessageCircle } from "lucide-react";
import SEO from "../components/SEO";
import AppointmentDialog from "../components/AppointmentDialog";
import { getBlogBySlug, BLOGS } from "../data/blogs";
import { SITE, callLink, whatsappLink } from "../lib/constants";
import useReveal from "../hooks/useReveal";

// Lightweight markdown-ish renderer for our blog content
function renderContent(md) {
  if (!md) return null;
  const lines = md.trim().split("\n");
  const out = [];
  let inTable = false;
  let tableRows = [];

  const flushTable = () => {
    if (!tableRows.length) return;
    const [head, , ...body] = tableRows;
    const headers = head
      .replace(/^\||\|$/g, "")
      .split("|")
      .map((c) => c.trim());
    const rows = body.map((r) =>
      r.replace(/^\||\|$/g, "").split("|").map((c) => c.trim())
    );
    out.push(
      <div key={`tbl-${out.length}`} className="my-8 overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className="px-5 py-3 text-left font-medium text-slate-700">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                {r.map((c, j) => (
                  <td key={j} className="px-5 py-3 text-slate-600">
                    {c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    tableRows = [];
  };

  let listItems = [];
  const flushList = () => {
    if (!listItems.length) return;
    out.push(
      <ul key={`ul-${out.length}`} className="my-5 space-y-2.5 pl-1">
        {listItems.map((li, k) => (
          <li key={k} className="flex items-start gap-3 text-slate-700">
            <div className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-2.5 flex-shrink-0" />
            <span dangerouslySetInnerHTML={{ __html: applyInline(li) }} />
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  const applyInline = (text) =>
    text
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-slate-900 font-semibold">$1</strong>')
      .replace(/\*([^*]+)\*/g, "<em>$1</em>")
      .replace(/`([^`]+)`/g, '<code class="text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded">$1</code>');

  lines.forEach((line, idx) => {
    if (line.startsWith("|")) {
      inTable = true;
      tableRows.push(line);
      return;
    } else if (inTable) {
      flushTable();
      inTable = false;
    }

    if (line.startsWith("- ") || /^\d+\. /.test(line)) {
      const txt = line.replace(/^- /, "").replace(/^\d+\. /, "");
      listItems.push(txt);
      return;
    } else if (listItems.length) {
      flushList();
    }

    if (line.startsWith("### ")) {
      out.push(
        <h3 key={idx} className="font-heading text-xl font-medium text-slate-900 mt-10 mb-3">
          {line.replace(/^### /, "")}
        </h3>
      );
    } else if (line.startsWith("## ")) {
      out.push(
        <h2 key={idx} className="font-heading text-2xl lg:text-3xl font-medium text-slate-900 mt-12 mb-4">
          {line.replace(/^## /, "")}
        </h2>
      );
    } else if (line.startsWith("> ")) {
      out.push(
        <blockquote
          key={idx}
          className="my-6 border-l-4 border-teal-500 bg-teal-50/50 px-5 py-3 italic text-slate-700"
          dangerouslySetInnerHTML={{ __html: applyInline(line.replace(/^> /, "")) }}
        />
      );
    } else if (line.trim()) {
      out.push(
        <p
          key={idx}
          className="text-slate-600 leading-relaxed my-4"
          dangerouslySetInnerHTML={{ __html: applyInline(line) }}
        />
      );
    }
  });
  flushList();
  flushTable();
  return out;
}

export default function BlogPost() {
  useReveal();
  const { slug } = useParams();
  const post = getBlogBySlug(slug);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOGS.filter((b) => b.slug !== post.slug).slice(0, 2);

  const articleStructured = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    image: post.cover,
    datePublished: post.date,
    author: { "@type": "Person", name: post.author },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_lotus-family-dental/artifacts/w0ba94vf_LOGO%20x%20png.png" },
    },
    description: post.excerpt,
  };

  return (
    <>
      <SEO
        title={`${post.title} | Lotus Family Dental`}
        description={post.excerpt}
        keywords={post.keywords?.join(", ")}
        canonical={`https://lotusfamilydental.me/blog/${post.slug}`}
        image={post.cover}
        structuredData={articleStructured}
      />

      <article className="pt-12 lg:pt-16 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-teal-700 mb-6">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
          <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold">{post.category}</div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-tight">
            {post.title}
          </h1>
          <div className="mt-5 flex items-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" /> {post.author}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> {post.readMinutes} min read
            </span>
            <span>•</span>
            <span>{post.date}</span>
          </div>
        </div>

        <div className="mt-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="aspect-[16/8] rounded-3xl overflow-hidden bg-slate-100">
            <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="mt-12 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-slate">
          {renderContent(post.content)}
        </div>

        {/* CTA */}
        <div className="mt-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 lg:p-10">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold">
              Take the next step
            </div>
            <h3 className="mt-2 font-heading text-2xl lg:text-3xl font-medium text-slate-900">
              Have a question about your dental care?
            </h3>
            <p className="mt-3 text-slate-600">
              Book a free consultation with our specialists at Lotus Family Dental, Allahabad.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={() => setDialogOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-teal-600 text-white font-medium hover:bg-teal-700"
                data-testid="blog-book-button"
              >
                Book Appointment <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href={callLink}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-200 text-slate-900 font-medium hover:bg-white"
              >
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white font-medium hover:bg-[#1faa55]"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>

        {/* Related */}
        <div className="mt-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-heading text-2xl font-medium text-slate-900 mb-6">Related Articles</h3>
          <div className="grid md:grid-cols-2 gap-5">
            {related.map((b) => (
              <Link
                key={b.slug}
                to={`/blog/${b.slug}`}
                className="group bg-white border border-slate-100 rounded-2xl overflow-hidden hover:shadow-md transition-all"
              >
                <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                  <img src={b.cover} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-wider text-teal-600 font-semibold">{b.category}</div>
                  <div className="mt-2 font-heading font-medium text-slate-900 group-hover:text-teal-700">{b.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </article>

      <AppointmentDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
